import { FC, useContext, useState } from 'react';
import { Button, Dialog, DialogContent, Theme, Typography, makeStyles, Box, DialogActions, Grid, TextField } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import Skeleton from '@material-ui/lab/Skeleton';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import JobActionTagIcon from '@material-ui/icons/AssignmentLate';
import UndoIcon from '@material-ui/icons/Undo';
import { GET_RESOLVE_JOB_ACTION_TAG_URL, GET_UNRESOLVE_JOB_ACTION_TAG_URL } from 'constants/url';
import axios, { CancelTokenSource } from 'axios';
import { CreateLogEvent } from 'utils/Firebase';
import { CurrentUserContext } from 'contexts/CurrentUserContext';
import { orange } from '@material-ui/core/colors';
import WarningIcon from '@material-ui/icons/Warning';
import theme from 'theme';

interface Props {
  isLoading: boolean;
  jobActionTags: JobActionTagModel[];
  jobId: number;
  fetchData(): void;
  handleSnackbar: (variant: 'success' | 'error', message: string) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  alertContainer: {
    marginBottom: theme.spacing(2)
  },
  unresolvedAlert: {
    backgroundColor: '#F5DADA',
    borderColor: theme.palette.error.main
  },
  alertHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  alertTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: 0
  },
  alertTitleText: {
    color: theme.palette.error.main
  },
  internalNoteBox: {
    fontStyle: 'italic'
  },
  resolvedAlert: {
    backgroundColor: '#E8F5E9',
    borderColor: theme.palette.success.main
  },
  resolvedHeader: {
    display: 'flex',
    alignItems: 'center'
  },
  resolvedContent: {
    backgroundColor: 'white',
    padding: theme.spacing(2),
    borderRadius: 4,
    marginTop: theme.spacing(1)
  },
  markResolvedButton: {
    backgroundColor: theme.palette.error.main,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.error.dark
    },
    whiteSpace: 'nowrap'
  },
  unresolveButton: {
    backgroundColor: theme.palette.warning.main,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.warning.dark
    },
    whiteSpace: 'nowrap'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  skeletonAlert: {
    marginBottom: theme.spacing(2)
  },
  button: {
    width: 200,
    marginRight: theme.spacing(2)
  }
}));

const JobActionTagContent: FC<Props> = props => {
  const classes = useStyles();
  const { currentUser } = useContext(CurrentUserContext);
  const { isLoading, jobActionTags = [], fetchData, handleSnackbar } = props;
  const [openResolutionModal, setOpenResolutionModal] = useState<boolean>(false);
  const [openUnresolveModal, setOpenUnresolveModal] = useState<boolean>(false);
  const [selectedActionTag, setSelectedActionTag] = useState<JobActionTagModel | null>(null);
  const [resolutionNote, setResolutionNote] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const unresolvedTags = jobActionTags.filter(tag => !tag.isResolved);
  const resolvedTags = jobActionTags.filter(tag => tag.isResolved);

  const handleMarkAsResolved = (tag: JobActionTagModel) => {
    setSelectedActionTag(tag);
    setOpenResolutionModal(true);
  };

  const handleUnresolveClick = (tag: JobActionTagModel) => {
    setSelectedActionTag(tag);
    setOpenUnresolveModal(true);
  };

  const handleConfirmResolution = () => {
    if (selectedActionTag) {
      handleMarkActionTagAsResolved(selectedActionTag.id);
      setOpenResolutionModal(false);
      setSelectedActionTag(null);
    }
  };

  const handleConfirmUnresolve = () => {
    if (selectedActionTag) {
      handleMarkActionTagAsUnresolved(selectedActionTag.id);
      setOpenUnresolveModal(false);
      setSelectedActionTag(null);
    }
  };

  if (isLoading) {
    return (
      <Box className={classes.skeletonAlert}>
        <Skeleton width={'80%'} height={40} />
        <Skeleton width={'100%'} height={60} style={{ marginTop: 16 }} />
      </Box>
    );
  }

  if (jobActionTags.length === 0) {
    return null;
  }

  const handleMarkActionTagAsResolved = async (actionTagId: number) => {
    setIsSaving(true);
    try {
      const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
      await axios.put(
        GET_RESOLVE_JOB_ACTION_TAG_URL(actionTagId),
        {
          resolutionNote
        },
        { cancelToken: cancelTokenSource.token }
      );

      fetchData();
      CreateLogEvent('resolve_action_tag', currentUser!);
      handleSnackbar('success', 'Action tag marked as resolved');
      setResolutionNote('');
      setIsSaving(false);
    } catch (err) {
      console.error(err);
      handleSnackbar('error', 'Failed to resolve action tag');
      setIsSaving(false);
    }
  };

  const handleMarkActionTagAsUnresolved = async (actionTagId: number) => {
    setIsSaving(true);
    try {
      const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
      await axios.put(GET_UNRESOLVE_JOB_ACTION_TAG_URL(actionTagId), {}, { cancelToken: cancelTokenSource.token });

      fetchData();
      CreateLogEvent('unresolve_action_tag', currentUser!);
      handleSnackbar('success', 'Action tag marked as unresolved');
      setIsSaving(false);
    } catch (err) {
      console.error(err);
      handleSnackbar('error', 'Failed to unresolve action tag');
      setIsSaving(false);
    }
  };

  return (
    <>
      {unresolvedTags.map((tag, index) => (
        <Alert
          key={`unresolved-${tag.id}`}
          icon={<JobActionTagIcon fontSize="inherit" />}
          severity='error'
          className={`${classes.alertContainer} ${classes.unresolvedAlert}`}
          style={{ borderStyle: 'solid', borderWidth: 'thin' }}
          action={
            <Button
              variant='contained'
              disableElevation
              size='small'
              className={classes.markResolvedButton}
              startIcon={<CheckCircleIcon />}
              onClick={() => handleMarkAsResolved(tag)}
            >
              Mark as Resolved
            </Button>
          }
        >
          <AlertTitle>
            <div className={classes.alertTitle}>
              <Typography variant='h5' gutterBottom>
                {tag.actionTag}
              </Typography>
            </div>
          </AlertTitle>
          <Typography variant='body2' className={classes.internalNoteBox}>
            <strong>{tag.internalNote || '-'}</strong>
          </Typography>
        </Alert>
      ))}

      {resolvedTags.map(tag => (
        <Alert
          key={`resolved-${tag.id}`}
          severity='success'
          className={`${classes.alertContainer} ${classes.resolvedAlert}`}
          style={{ borderStyle: 'solid', borderWidth: 'thin' }}
          action={
            <Button
              variant='contained'
              disableElevation
              size='small'
              className={classes.unresolveButton}
              startIcon={<UndoIcon />}
              onClick={() => handleUnresolveClick(tag)}
            >
              Unresolve
            </Button>
          }
        >
          <AlertTitle>
            <div className={classes.resolvedHeader}>
              <Typography variant='h5' gutterBottom>
                Resolved - {tag.actionTag}
              </Typography>
            </div>
          </AlertTitle>
          <Typography variant='body2' className={classes.internalNoteBox}>
            <strong>{tag.internalNote || '-'}</strong>
          </Typography>
          <Typography variant='body2' className={classes.resolvedContent}>
            <strong>Resolution Note: {tag.resolutionNote || '-'} </strong>
          </Typography>
        </Alert>
      ))}

      {openResolutionModal && selectedActionTag && (
        <Dialog open={openResolutionModal} onClose={() => setOpenResolutionModal(false)} maxWidth='sm' fullWidth>
          <DialogContent dividers>
            <Grid container justify='center' alignItems='center'>
              <WarningIcon style={{ fontSize: 80, color: orange[500], marginBottom: theme.spacing(2) }} />
            </Grid>
            <Typography variant='h4' align='center' gutterBottom>
              Are you sure you want to mark this action tag as resolved?
            </Typography>
            <Grid container alignItems='center' style={{ marginTop: 16 }}>
              <TextField
                margin='dense'
                fullWidth
                id='resolutionNote'
                variant='outlined'
                label='Resolution Note (Optional)'
                placeholder='Please enter resolution note here'
                multiline
                rows={5}
                value={resolutionNote}
                onChange={event => setResolutionNote(event.target.value)}
              />
            </Grid>
            <DialogActions style={{ marginTop: 16 }}>
              <Grid container justify='center' alignItems='center'>
                <Button
                  variant='contained'
                  disableElevation
                  className={classes.button}
                  onClick={() => setOpenResolutionModal(false)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  disableElevation
                  className={classes.button}
                  color='primary'
                  onClick={handleConfirmResolution}
                  disabled={isSaving}
                >
                  Confirm
                </Button>
              </Grid>
            </DialogActions>
          </DialogContent>
        </Dialog>
      )}

      {openUnresolveModal && selectedActionTag && (
        <Dialog open={openUnresolveModal} onClose={() => setOpenUnresolveModal(false)} maxWidth='sm' fullWidth>
          <DialogContent dividers>
            <Grid container justify='center' alignItems='center'>
              <WarningIcon style={{ fontSize: 80, color: orange[500], marginBottom: theme.spacing(2) }} />
            </Grid>
            <Typography variant='h4' align='center' gutterBottom>
              Are you sure you want to unresolve this action tag?
            </Typography>
            <DialogActions style={{ marginTop: 32 }}>
              <Grid container justify='center' alignItems='center'>
                <Button
                  variant='contained'
                  disableElevation
                  className={classes.button}
                  onClick={() => setOpenUnresolveModal(false)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  disableElevation
                  className={classes.button}
                  color='primary'
                  onClick={handleConfirmUnresolve}
                  disabled={isSaving}
                >
                  Confirm
                </Button>
              </Grid>
            </DialogActions>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default JobActionTagContent;
